// Graph API email sending for new vacancies (spec §8.1).
import { type Vacancy } from "../fs/vacancies";

/**
 * Sends a notification email via Microsoft Graph API to all registered consultants
 * on behalf of the user who captured the vacancy.
 */
export async function sendNewVacancyEmail(
  accessToken: string,
  vacancy: Vacancy,
  creatorName: string,
  recipientEmails: string[],
): Promise<void> {
  if (recipientEmails.length === 0) return;

  const earliestRoute = vacancy.routes[0];
  const rateStr = earliestRoute?.rate ? `€${earliestRoute.rate}/uur` : "Niet vermeld";
  
  // Format deadline nicely
  const deadlineStr = earliestRoute?.deadline
    ? new Date(earliestRoute.deadline).toLocaleDateString("nl-NL", {
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }) + " ⚠️"
    : "Geen deadline";

  const emailBody = `
Er is een nieuwe vacature toegevoegd aan de Lens bibliotheek.

Functie:      ${vacancy.title}
Opdrachtgever: ${vacancy.client}
Portaal:      ${earliestRoute?.portal || "Onbekend"}
Tarief:       ${rateStr}
Deadline:     ${deadlineStr}
Locatie:      ${vacancy.location} (${vacancy.remote_policy})

Toegevoegd door: ${creatorName}

Open Lens om te analyseren en een workflow te starten.
  `.trim();

  const payload = {
    message: {
      subject: `[Lens] Nieuwe vacature: ${vacancy.title} — ${vacancy.client}`,
      body: {
        contentType: "Text",
        content: emailBody,
      },
      toRecipients: recipientEmails.map((email) => ({
        emailAddress: {
          address: email,
        },
      })),
    },
    saveToSentItems: false,
  };

  try {
    const response = await fetch("https://graph.microsoft.com/v1.0/me/sendMail", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errText = await response.text();
      console.error("Microsoft Graph mail send error:", errText);
      throw new Error(`Graph API sendMail returned status ${response.status}: ${errText}`);
    }
  } catch (err) {
    console.error("Failed to send email via Graph API", err);
    throw err;
  }
}
